import React, { useState, useEffect, useRef, useCallback } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import './CrimeScene.css';
import '../../ui/ArcadeShared.css';

const CrimeScene = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  const [state, setState] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shakingIndices, setShakingIndices] = useState([]);
  const [floatingPenalty, setFloatingPenalty] = useState(false);
  const [finalData, setFinalData] = useState(null);

  const timeoutRef = useRef(null); // Храним ID таймера

  // Вспомогательная функция форматирования времени
  const formatTime = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = (totalSec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Функция принудительного завершения анимации
  const forceFinishAnimation = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShakingIndices([]);
    setIsAnimating(false);
    // Если карты были открыты (mismatch), шлем закрытие.
    // Для match это не вредит, сервер просто проигнорирует или вернет тот же стейт.
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'close_pair' });
  }, [store.ref.work.methods]);

  const handlePairResult = useCallback((data) => {
    setState(data.state);
    setIsAnimating(true);

    const result = data.result;

    if (result === 'match') {
      window.croco?.haptic?.('success');
      if (data.gameFinished) {
          setTimeout(() => setFinalData({ ...data.finalData }), 1500);
      } else {
          // Матч показываем 1 сек
          timeoutRef.current = setTimeout(() => setIsAnimating(false), 1000);
      }
    } else {
      // ОШИБКА
      // Ждем 1.5 сек перед закрытием
      timeoutRef.current = setTimeout(() => {
        window.croco?.haptic?.('error');

        if (result === 'penalty') {
          setFloatingPenalty(true);
          setTimeout(() => setFloatingPenalty(false), 1200);
        }

        const flippedIndices = data.state.grid
            .map((c, i) => c.isFlipped && !c.solved ? i : -1)
            .filter(i => i !== -1);

        setShakingIndices(flippedIndices);

        // Трясем и закрываем
        timeoutRef.current = setTimeout(() => {
          forceFinishAnimation();
        }, 500);
      }, 1500);
    }
  }, [forceFinishAnimation]);

  useEffect(() => {
    if (!socket) return;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
      if (data.type === 'update') setState(data.state);
      else if (data.type === 'pair_result') handlePairResult(data);
      else if (data.type === 'win') setFinalData(data); // Фоллбэк
      else if (data.type === 'game_over') setFinalData(data);
    };

    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
  }, [socket, handlePairResult, store.ref.work.methods]);

  const handleCardClick = (index) => {
    if (!state) return;

    // Если клик по уже открытой/решенной карте - игнор
    if (state.grid[index].solved || state.grid[index].isFlipped) return;

    // FIX: Быстрый ход
    // Если идет анимация (игрок смотрит на результат прошлого хода),
    // мы прерываем ожидание и сразу делаем новый ход.
    if (isAnimating) {
       forceFinishAnimation();
    }

    if (state.isBusy && !isAnimating) return; // Защита от дабл-клика в процессе отправки

    window.croco?.haptic?.('selection');
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'flip', index });
  };

  // --- БЛОК РЕНДЕРА РЕЗУЛЬТАТОВ (ОБНОВЛЕННЫЙ) ---
  if (finalData) {
    const { score, mistakes, timeSpent, rank } = finalData.my || {};

    return (
      <div className="cs-result-overlay">
         <div className="cs-report-card">

             {/* Декоративный водяной знак на фоне */}
             <div className="cs-watermark">CONFIDENTIAL</div>

             {/* Заголовок */}
             <div className="cs-report-header">
                <div className="cs-report-title">
                  {t('arcade.cs_report_title', 'РАПОРТ')}
                </div>
             </div>

             {/* Статистика */}
             <div className="cs-stats-grid">

                {/* Ранг */}
                <div className="cs-stat-row">
                   <span className="cs-stat-label">{t('arcade.cs_rank_title', 'ЗВАНИЕ')}</span>
                   {/* Добавили класс rank-text */}
                   <span className="cs-stat-value rank-text">
                      {t(`arcade.${rank}`, 'Детектив')}
                   </span>
                </div>

                {/* Время */}
                <div className="cs-stat-row">
                   <span className="cs-stat-label">{t('arcade.cs_stat_time', 'ВРЕМЯ')}</span>
                   <span className="cs-stat-value">{formatTime(timeSpent)}</span>
                </div>

                {/* Ошибки */}
                <div className="cs-stat-row">
                   <span className="cs-stat-label">{t('arcade.cs_stat_mistakes', 'ОШИБКИ')}</span>
                   <span className={`cs-stat-value ${mistakes > 0 ? 'bad' : 'good'}`}>
                      {mistakes}
                   </span>
                </div>

                {/* Счет */}
                <div className="cs-stat-row" style={{borderBottom: '2px solid #333', paddingBottom: '10px'}}>
                   <span className="cs-stat-label">{t('arcade.cs_stat_score', 'ИТОГ')}</span>
                   <span className="cs-stat-value" style={{fontSize: '32px'}}>
                      {score}
                   </span>
                </div>
             </div>

             {/* Штамп поверх всего */}
             <div className="cs-stamp">
                {t('arcade.cs_case_closed', 'ЗАКРЫТО')}
             </div>

             <button className="cs-close-btn" onClick={onClose}>
                {t('arcade.close_dossier', 'ПОДШИТЬ В АРХИВ')}
             </button>
         </div>
      </div>
    );
  }

  if (!state) return null;

  const renderGrid = () => {
    if (!state?.gridSize) return null;

    const { cols, rows } = state.gridSize;

    // Базовый размер 42px для сетки 4x4. Чем больше элементов, тем меньше шрифт.
    const fontSize = cols <= 4 ? 42 :
    cols <= 5 ? 35 : 
    cols <= 6 ? 30 : 
    25;

    // Динамический расчет расстояния (gap)
    // Чем меньше колонок (крупнее карты), тем больше зазор.
    // Например: 3 колонки -> 13.3px, 6 колонок -> 6.6px
    const gapSize = (1 / cols) * 45;

    const gridStyle = {
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: `${gapSize}px`, // Применяем динамический зазор
      aspectRatio: `${cols} / ${rows}`,
    };

    return (
      <div className="cs-grid" style={gridStyle}>
        {state.grid.map((card, idx) => {
          const isFlipped = card.isFlipped || card.solved;
          let cardClass = "cs-card";
          if (isFlipped) cardClass += " flipped";
          if (card.solved) cardClass += " solved-visual";

          let containerClass = "cs-card-container";
          if (card.solved) containerClass += " solved";
          if (shakingIndices.includes(idx)) containerClass += " shake";

          const emojiToRender = card.emoji || '';

          return (
            <div key={idx} className={containerClass} onClick={() => handleCardClick(idx)}>
              <div className={cardClass}>
                <div className="cs-card-front"></div>
                <div
                  className="cs-card-back"
                  style={{ fontSize: `${fontSize}px` }} // Динамический шрифт
                >
                  {emojiToRender}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="crimescene-board">
      <div className="quiz-timer" style={{top: '-20px', right: '10px'}}>
         {Math.floor(timeLeft / 60).toString().padStart(2, '0')}:{(timeLeft % 60).toString().padStart(2, '0')}
      </div>

      {floatingPenalty && <div className="penalty-floating">-1</div>}

      <div className="crimescene-header">
        <div className="cs-title">{t('arcade.crimescene_title', 'УЛИКИ')}</div>
        <div className="cs-stats">
          {t('arcade.crimescene_score', 'СЧЕТ')}: <span className="cs-score">{state.score}</span>
          <span style={{margin: '0 8px', opacity: 0.3}}>|</span>
          {t('arcade.crimescene_mistakes', 'ОШИБКИ')}: <span className="cs-penalty">{state.mistakes}</span>
        </div>
      </div>

      {renderGrid()}
    </div>
  );
};

export default CrimeScene;